import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LABELS = {
  athletes: "atleti",
  groups: "gruppi",
  exercises: "esercizi",
  tests: "test",
  competitions: "gare",
};

export default function ImportDialog({ entity, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const label = LABELS[entity] || entity;

  const downloadTemplate = () => {
    const token = localStorage.getItem("csp_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/import/${entity}/template`;
    // Fetch as blob to include auth
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("Errore download"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `template_${entity}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Errore nel download del template"));
  };

  const doUpload = async () => {
    if (!file) return toast.error("Seleziona un file");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} ${label} importati`);
        onDone?.();
      } else if (data.errors?.length) {
        toast.error("Nessun record importato");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>
        {trigger || (
          <Button data-testid={`import-${entity}-btn`} variant="outline" className="h-12"><Upload className="w-4 h-4 mr-2" />Importa</Button>
        )}
      </div>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Importa {label} da Excel</DialogTitle>
          <DialogDescription>Carica un file .xlsx compilato secondo il modello. Ogni riga sarà creata come nuovo record.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-zinc-500 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium">1. Scarica il modello</div>
              <div className="text-xs text-zinc-500 mb-2">Include colonne, esempio e istruzioni.</div>
              <Button data-testid={`download-template-${entity}`} onClick={downloadTemplate} size="sm" variant="outline"><Download className="w-3 h-3 mr-1" />Scarica template</Button>
            </div>
          </div>

          <div>
            <div className="text-sm font-medium mb-1">2. Carica il file compilato</div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid={`file-input-${entity}`}
              className="block w-full text-sm text-zinc-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-black file:text-white hover:file:bg-zinc-800 file:cursor-pointer"
            />
            {file && <div className="text-xs text-zinc-500 mt-1 font-mono">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>

          {result && (
            <div className="border border-zinc-200 rounded-md p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span><strong className="font-mono">{result.inserted}</strong> record importati su <span className="font-mono">{result.total_rows}</span></span>
              </div>

              {result.recognized_columns?.length > 0 && (
                <div data-testid="import-recognized">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Colonne riconosciute ({result.recognized_columns.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {result.recognized_columns.map((c, i) => (
                      <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        “{c.header}” → {c.field}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {result.ignored_columns?.length > 0 && (
                <div data-testid="import-ignored">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Colonne ignorate ({result.ignored_columns.length})</div>
                  <div className="space-y-1">
                    {result.ignored_columns.map((c, i) => (
                      <div key={i} className="text-[11px] font-mono px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                        “{c.header || `col ${c.index + 1}`}” — {c.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.errors?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-red-600 mb-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{result.errors.length} errori di conversione</span>
                  </div>
                  <div className="max-h-32 overflow-y-auto no-scrollbar text-xs space-y-1 bg-red-50 border border-red-100 rounded p-2">
                    {result.errors.slice(0, 20).map((e, i) => (
                      <div key={i} className="font-mono">Riga {e.row}: {e.error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" onClick={reset}>Nuovo import</Button>
              <Button onClick={() => setOpen(false)} className="bg-black hover:bg-zinc-800">Chiudi</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid={`import-submit-${entity}`} onClick={doUpload} disabled={!file || loading} className="bg-black hover:bg-zinc-800">
                {loading ? "Importazione…" : "Importa"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
